import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import { HeaderRight } from '../../../layout/Header/Header';

import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Icon from '../../../components/icon/Icon';
import LanguageSelector from '../../../components/LanguageSelector';
import showNotification from '../../../components/extras/showNotification';
import useDarkMode from '../../../hooks/useDarkMode';
import Popovers from '../../../components/bootstrap/Popovers';
import Nav, { NavItem } from '../../../components/bootstrap/Nav';
import { useShoppingCart } from '../../../contexts/shoppingCartContext';
import AuthContext from '../../../contexts/authContext';
import { clearAllAppData } from '../../../helpers/authHelpers';
import AccountMenuItems from './AccountMenuItems';

const MainPageHeaderRight = () => {
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { user, setUser } = useContext(AuthContext);
	const loggedIn = !!user && !!localStorage.getItem('token');
	const { cartQuantity } = useShoppingCart();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const navLinkStyle = (isActive: boolean) => ({
		textDecoration: 'none',
		fontWeight: isActive ? 600 : 400,
		borderBottom: isActive ? '2px solid currentColor' : '2px solid transparent',
		paddingBottom: 2,
		transition: 'border-color 0.2s ease, font-weight 0.2s ease',
	});

	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const serverUrl = process.env.REACT_APP_SERVER_URL;
	const [availableTypes, setAvailableTypes] = useState<string[]>([]);

	// Map of product type key → display metadata (same as ProductsPage)
	const typeMetaMap: Record<string, { label: string; icon: string }> = {
		storybook:    { label: t('Stories'),        icon: 'AutoStories' },
		coloringbook: { label: t('Coloring Books'), icon: 'Palette' },
		tshirt:       { label: t('T-Shirts'),       icon: 'Checkroom' },
		mug:          { label: t('Mugs'),           icon: 'LocalCafe' },
		calendar:     { label: t('Calendars'),      icon: 'CalendarToday' },
		poster:       { label: t('Posters'),        icon: 'Image' },
		hoodie:       { label: t('Hoodies'),        icon: 'Backpack' },
		phonecase:    { label: t('Phone Cases'),    icon: 'PhoneIphone' },
		totebag:      { label: t('Tote Bags'),      icon: 'ShoppingBag' },
		puzzle:       { label: t('Puzzles'),        icon: 'Extension' },
	};

	const productTypeItems = useMemo(() => {
		return availableTypes.map((typeKey) => {
			const meta = typeMetaMap[typeKey] || { label: typeKey, icon: 'Category' };
			return { key: typeKey, label: meta.label, icon: meta.icon };
		});
	}, [availableTypes, t]);

	useEffect(() => {
		fetch(`${serverUrl}/api/products/types`)
			.then((res) => (res.ok ? res.json() : Promise.reject('Failed to fetch types')))
			.then((data) => {
				const types = data.response || data || [];
				setAvailableTypes(Array.isArray(types) ? types : []);
			})
			.catch((err) => console.error('Error fetching product types:', err));
	}, [serverUrl]);

	return (
		<HeaderRight>

			<div className='row g-3'>
                <div className='col-auto'>
                <Nav style={{marginTop: 5}}>
                    <NavItem >
                        <a href='/' onClick={(e) => { e.preventDefault(); navigate('/'); }} style={navLinkStyle(location.pathname === '/')}>{t("Home")}</a>
                    </NavItem>
                    <NavItem style={{marginLeft: 10}}>
						<Dropdown className=''>
							<DropdownToggle hasIcon={false}>
								<span
									role='button'
									tabIndex={0}
									className='nav-link'
									style={{
										...navLinkStyle(location.pathname.startsWith('/products')),
										cursor: 'pointer',
									}}
								>
									{t("Products")}
								</span>
							</DropdownToggle>
							<DropdownMenu>
								<DropdownItem>
									<Button icon='Apps' onClick={() => navigate('/products')}>{t('All Products')}</Button>
								</DropdownItem>
								{productTypeItems.map((item) => (
									<DropdownItem key={item.key}>
										<Button icon={item.icon} onClick={() => navigate(`/products?type=${item.key}`)}>{item.label}</Button>
									</DropdownItem>
								))}
							</DropdownMenu>
						</Dropdown>
                    </NavItem>
                    {/* Account - text link when logged out, dropdown when logged in.
                        Hidden below md: the compact mobile header bar (see
                        MobileHeaderActions) already shows this always, so it
                        isn't buried in the hamburger menu on phone screens --
                        duplicating it here too would just crowd that panel. */}
                    {!loggedIn ? (
                        <NavItem className='d-none d-md-block' style={{marginLeft: 10}}>
                            <a href='/auth' onClick={(e) => { e.preventDefault(); navigate('/auth'); }} style={navLinkStyle(location.pathname === '/auth')}>{t("Sign in")}</a>
                        </NavItem>
                    ) : (
                        <NavItem className='d-none d-md-block' style={{marginLeft: 10}}>
                            <Dropdown>
                                <DropdownToggle hasIcon={false}>
                                    <span
                                        role='button'
                                        tabIndex={0}
                                        className='nav-link'
                                        style={{
                                            ...navLinkStyle(['/my-orders', '/my-avatars', '/my-cart'].some((p) => location.pathname.startsWith(p))),
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {t("My Account")}
                                    </span>
                                </DropdownToggle>
                                <DropdownMenu isAlignmentEnd size='lg' className='py-0 overflow-hidden'>
                                    <AccountMenuItems
                                        darkModeStatus={darkModeStatus}
                                        navigate={navigate}
                                        onSignOut={() => {
                                            clearAllAppData();
                                            if (setUser) setUser('');
                                            window.location.href = '/';
                                        }}
                                    />
                                </DropdownMenu>
                            </Dropdown>
                        </NavItem>
                    )}

                </Nav>
                </div>
				

				{/* Dark/Light mode toggle */}
				<div className='col-auto'>
					<Button
						{...styledBtn}
						onClick={() => setDarkModeStatus(!darkModeStatus)}
						className='btn-only-icon'
						aria-label='Toggle dark mode'
						icon={darkModeStatus ? 'LightMode' : 'DarkMode'}
					/>
				</div>

				{/* Lang Selector */}
				<div className='col-auto'>
					<LanguageSelector />
				</div>

				{/* Cart — hidden below md: the compact mobile header bar (see
				    MobileHeaderActions) already shows this always. */}
				{true && <div className='col-auto d-none d-md-block'>
					<Button
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...styledBtn}
						onClick={() => navigate('/my-cart')}
						className='btn-only-icon position-relative'
						aria-label='Open cart'
						id='header-cart-icon'
						>
						<Icon icon='ShoppingCart' color={darkModeStatus ? 'light' : 'dark'} className='btn-icon' />
						{cartQuantity > 0 && (
							<span
								className='position-absolute badge rounded-pill bg-danger'
								style={{
									top: 4,
									right: 2,
									fontSize: 10,
									fontWeight: 700,
									minWidth: 18,
									height: 18,
									lineHeight: '18px',
									padding: '0 5px',
								}}>
								{cartQuantity}
							</span>
						)}
					</Button>
				</div>}
			</div>
		</HeaderRight>
	);
};

export default MainPageHeaderRight;
